/** Pomocné funkcie pre formulár produktu: jednotky, hmotnosť, trvanlivosť, alergény a B2B cena. */

export const UNIT_OPTIONS = [
  { value: 'ks', label: 'kus (ks)' },
  { value: 'kg', label: 'kilogram (kg)' },
  { value: 'l', label: 'liter (l)' },
] as const

/** Zjednotí zápis jednotky z pokladne („Ks“, „KS“, „liter“ …) na ks / kg / l; neznámu ponechá. */
export function normalizeUnit(unit: string | null | undefined): string {
  const value = (unit ?? '').trim().toLocaleLowerCase('sk')
  if (!value || ['ks', 'kus', 'kusy', 'ks.'].includes(value)) return 'ks'
  if (['kg', 'kilogram'].includes(value)) return 'kg'
  if (['l', 'liter', 'litre', 'lit'].includes(value)) return 'l'
  return (unit ?? '').trim()
}

export type WeightUnit = 'g' | 'kg'

/** Text pre pole hmotnosti v zvolenej jednotke (gramy z databázy). */
export function weightToInput(grams: number | null | undefined, unit: WeightUnit): string {
  if (!grams) return ''
  return unit === 'kg' ? String(Math.round(grams) / 1000).replace('.', ',') : String(grams)
}

/** Hmotnosť z poľa formulára prevedená na celé gramy (null = nezadaná, NaN = neplatná). */
export function inputToGrams(value: string, unit: WeightUnit): number | null {
  const text = value.trim().replace(/\s/g, '').replace(',', '.')
  if (!text) return null
  const number = Number(text)
  if (!Number.isFinite(number) || number <= 0) return NaN
  return Math.round(unit === 'kg' ? number * 1000 : number)
}

/** Kilogramy predvolíme, ak je hmotnosť aspoň 1 kg. */
export function preferredWeightUnit(grams: ReadonlyArray<number | null | undefined>): WeightUnit {
  const known = grams.filter((g): g is number => typeof g === 'number' && g > 0)
  return known.length > 0 && known.every(g => g >= 1000) ? 'kg' : 'g'
}

export type VolumeUnit = 'ml' | 'l'

/** Text pre pole objemu v zvolenej jednotke (mililitre z databázy). */
export function volumeToInput(ml: number | null | undefined, unit: VolumeUnit): string {
  if (!ml) return ''
  return unit === 'l' ? String(Math.round(ml) / 1000).replace('.', ',') : String(ml)
}

/** Objem z poľa formulára prevedený na celé mililitre (null = nezadaný, NaN = neplatný). */
export function inputToMl(value: string, unit: VolumeUnit): number | null {
  const text = value.trim().replace(/\s/g, '').replace(',', '.')
  if (!text) return null
  const number = Number(text)
  if (!Number.isFinite(number) || number <= 0) return NaN
  return Math.round(unit === 'l' ? number * 1000 : number)
}

/** Litre predvolíme, ak je objem aspoň 1 l. */
export function preferredVolumeUnit(ml: ReadonlyArray<number | null | undefined>): VolumeUnit {
  const known = ml.filter((v): v is number => typeof v === 'number' && v > 0)
  return known.length > 0 && known.every(v => v >= 1000) ? 'l' : 'ml'
}

export type MeasureKind = 'none' | 'weight' | 'volume'

/** Podľa toho, čo je v karte produktu vyplnené (hmotnosť alebo objem – vzájomne sa vylučujú). */
export function measureKindOf(weightGrams: number | null | undefined, volumeMl: number | null | undefined): MeasureKind {
  if (volumeMl) return 'volume'
  if (weightGrams) return 'weight'
  return 'none'
}

export type ShelfUnit = 'DAY' | 'MONTH'
export type ShelfLife = { value: string; unit: ShelfUnit; note: string }

const DAY_WORDS = ['deň', 'den', 'dni', 'dní', 'dní', 'dňa', 'dna']
const MONTH_WORDS = ['mesiac', 'mesiace', 'mesiacov', 'mes.', 'mes']

export function shelfUnitWord(count: number, unit: ShelfUnit): string {
  if (unit === 'MONTH') return count === 1 ? 'mesiac' : count >= 2 && count <= 4 ? 'mesiace' : 'mesiacov'
  return count === 1 ? 'deň' : count >= 2 && count <= 4 ? 'dni' : 'dní'
}

/** Rozloží uložený text („3 dni, pri 2–6 °C“) na počet, jednotku a poznámku o skladovaní. */
export function parseShelfLife(text: string | null | undefined): ShelfLife {
  const value = (text ?? '').trim()
  const match = /^(\d{1,4})\s*([^\s,;]+)(?=$|[\s,;])[,;]?\s*(.*)$/u.exec(value)
  if (match) {
    const word = match[2].toLocaleLowerCase('sk')
    if (DAY_WORDS.includes(word)) return { value: match[1], unit: 'DAY', note: match[3] }
    if (MONTH_WORDS.includes(word)) return { value: match[1], unit: 'MONTH', note: match[3] }
  }
  return { value: '', unit: 'DAY', note: value }
}

/** Zloží text trvanlivosti pre zákazníka, napr. „3 dni, skladovať pri 2–6 °C“. */
export function formatShelfLife(shelf: ShelfLife): string {
  const count = Number(shelf.value)
  const note = shelf.note.trim()
  if (!shelf.value.trim() || !Number.isInteger(count) || count <= 0) return note
  const main = `${count} ${shelfUnitWord(count, shelf.unit)}`
  return note ? `${main}, ${note}` : main
}

/** 14 alergénov podľa prílohy II nariadenia EÚ 1169/2011. */
export const ALLERGENS = [
  { id: 1, name: 'lepok' },
  { id: 2, name: 'kôrovce' },
  { id: 3, name: 'vajcia' },
  { id: 4, name: 'ryby' },
  { id: 5, name: 'arašidy' },
  { id: 6, name: 'sója' },
  { id: 7, name: 'mlieko' },
  { id: 8, name: 'orechy' },
  { id: 9, name: 'zeler' },
  { id: 10, name: 'horčica' },
  { id: 11, name: 'sezam' },
  { id: 12, name: 'siričitany' },
  { id: 13, name: 'vlčí bôb' },
  { id: 14, name: 'mäkkýše' },
] as const

export type B2bPricingMode = 'NONE' | 'DISCOUNT' | 'FIXED'

/**
 * Veľkoobchodná cena B2B – rovnaký výpočet ako na serveri (src/b2b-pricing.ts), aby sa v administrácii
 * zobrazil okamžitý náhľad (bez DPH aj s DPH) bez volania servera. "DISCOUNT" počíta zľavu z maloobchodnej
 * ceny, "FIXED" berie zadanú pevnú cenu bez DPH. DPH sa použije z karty produktu, inak 23 %.
 */
export function computeB2bPriceCents(input: { priceCents: number; b2bPricingMode: B2bPricingMode | string; b2bDiscountPercent: number | null; b2bFixedPriceCents: number | null; vatRate: number | null }): { exclVatCents: number; inclVatCents: number } | null {
  const vat = input.vatRate ?? 23
  if (input.b2bPricingMode === 'DISCOUNT' && input.b2bDiscountPercent != null) {
    const inclVatCents = Math.round(input.priceCents * (1 - input.b2bDiscountPercent / 100))
    const exclVatCents = Math.round(inclVatCents / (1 + vat / 100))
    return { exclVatCents, inclVatCents }
  }
  if (input.b2bPricingMode === 'FIXED' && input.b2bFixedPriceCents != null) {
    const exclVatCents = input.b2bFixedPriceCents
    const inclVatCents = Math.round(exclVatCents * (1 + vat / 100))
    return { exclVatCents, inclVatCents }
  }
  return null
}

export const NO_ALLERGENS_TEXT = 'Neobsahuje alergény'

export type AllergenState = { ids: number[]; none: boolean; legacy: string }

/** Z uloženého textu zistí zaškrtnuté alergény (čísla 1–14). Iný voľný text vráti ako „legacy“. */
export function parseAllergens(text: string | null | undefined): AllergenState {
  const value = (text ?? '').trim()
  if (!value) return { ids: [], none: false, legacy: '' }
  if (/^neobsahuje/i.test(value)) return { ids: [], none: true, legacy: '' }
  const ids = [...new Set([...value.matchAll(/(?<!\d)(1[0-4]|[1-9])(?!\d)/g)].map(m => Number(m[1])))].sort((a, b) => a - b)
  return { ids, none: false, legacy: ids.length ? '' : value }
}

/** Text pre zákazníka, napr. „1 – lepok, 3 – vajcia, 7 – mlieko“. */
export function formatAllergens(ids: readonly number[], none = false): string {
  if (none) return NO_ALLERGENS_TEXT
  return [...new Set(ids)].sort((a, b) => a - b)
    .map(id => ALLERGENS.find(a => a.id === id))
    .filter((a): a is (typeof ALLERGENS)[number] => Boolean(a))
    .map(a => `${a.id} – ${a.name}`)
    .join(', ')
}
